import Divider from 'antd/es/divider';
import type { MenuProps } from 'antd/es/menu';
import Menu from 'antd/es/menu';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoFull from '../../../../assets/logo_rectangle.png';
import './styles.css';
import { ItemType } from 'antd/es/menu/interface';
import FAIcon from '../../FAIcon/FAIcon';
import { FormattedMessage, useIntl } from 'react-intl';
import Avatar from 'antd/es/avatar';
import Network from '../../../../utils/Network';
import { reset as resetUserSlice } from '../../../../store/features/userSlice';
import useDevice from '../../../../hooks/useDevice';
import useUser from '../../../../hooks/useUser';
import { Spin } from 'antd';
import { useUpdateContext } from '../../../../contexts/updateContext';
import LanguageSwitcher from '../../languageSwitcher/languageSwitcher';

type SideMenuItem = {
    path: string,
    icon: ReactNode;
    label: ReactNode | undefined;
    visible?: boolean;
    className?: string;
    children?: SideMenuItem[];
    onClick?: () => void;
} | 'divider';

interface Props {
    onRouteChange?: () => void;
}

const SideMenu = (props: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { isSmartphone } = useDevice();
    const user = useUser();

    const { loading, refreshVersion } = useUpdateContext();

    const [openKeys, setOpenKeys] = useState<string[]>([]);

    const version = useMemo(() => import.meta.env.VITE_APP_VERSION, []);

    const lang = useMemo(() => {
        return window.location.pathname.split('/')[1];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.pathname]);

    const goTo = useCallback((link: string) => {
        const newUrl = `/${lang}/${link}`;
        //! To change with navigate from useNavigate when updating react router

        if (props.onRouteChange)
            props.onRouteChange();

        if (newUrl !== window.location.pathname)
            navigate(newUrl);
    }, [lang, navigate, props]);

    const selectedKeys = useMemo(() => {
        const splittedUrl = window.location.pathname.split("/");

        const keys: string[] = [];
        let currentUrl = '';

        for (let i = 2; i < splittedUrl.length; i++) {
            currentUrl += splittedUrl[i];
            keys.push(currentUrl);
            currentUrl += '/';
        }

        return keys;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.pathname]);

    useEffect(() => {
        setOpenKeys(selectedKeys);
    }, [selectedKeys]);

    const resetReduxStore = useCallback(() => {
        resetUserSlice();
    }, []);

    const handleLogout = useCallback(() => {
        Network.logout().then(
            () => {
                resetReduxStore();
                navigate(`/${lang}/login`);
            },
        );
    }, [lang, navigate, resetReduxStore]);


    const sideMenuItems = useMemo((): SideMenuItem[] => [
        {
            path: 'sunkhronos/companies',
            icon: <FAIcon prefix='fad' name='grid-2-plus' />,
            label: "Sunkhronos"
        }
    ], []);

    const footerMenuItems = useMemo((): SideMenuItem[] => [
        {
            path: 'update',
            icon: <Spin spinning={loading}><FAIcon prefix='fad' name='rotate' /></Spin>,
            label: version,
            onClick: () => refreshVersion()
        }
    ], [loading, refreshVersion, version]);

    const onOpenChange = (keys: string[]) => {
        const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1);
        setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    };

    const convertToAntdMenuItems = useCallback((items: SideMenuItem[], path?: string, openKeys?: string[], setOpenKeys?: (keys: string[]) => void): MenuProps['items'] => {
        return items
            .filter((item) => {
                if (typeof item === 'string') return true;
                return item.visible !== false;
            })
            .map((item, index) => {
                if (typeof item === 'string') {
                    return {
                        type: 'divider',
                        key: `divider-${index}`
                    };
                }

                const fullPath = path ? `${path}/${item.path}` : item.path;

                const menuItem: ItemType = {
                    key: fullPath,
                    icon: item.icon,
                    label: item.children && item.children.length > 0 ? (
                        <span
                            onClick={(e) => {
                                e.stopPropagation(); // Empêche l'ouverture/fermeture du dropdown
                                if (item.onClick) {
                                    item.onClick();
                                } else {
                                    const firstVisibleChildren = item.children?.filter((i: any) => i.visible === undefined || i.visible === true);

                                    // Navigation vers la première page enfant ou page principale
                                    const targetPath = firstVisibleChildren && firstVisibleChildren.length > 0
                                        ? `${fullPath}/${(firstVisibleChildren[0] as any).path}`
                                        : fullPath;
                                    goTo(targetPath);
                                }
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            {item.label}
                        </span>
                    ) : item.label,
                    className: item.className,
                    onClick: !item.children ? () => {
                        if (item.onClick) {
                            item.onClick();
                        } else {
                            goTo(fullPath);
                        }
                    } : undefined,
                    children: item.children ? convertToAntdMenuItems(item.children, fullPath, openKeys, setOpenKeys) : undefined
                };

                return menuItem;
            });
    }, [goTo]);

    const antdMenuItems = useMemo(() => convertToAntdMenuItems(sideMenuItems), [convertToAntdMenuItems, sideMenuItems]);
    const antdFooterMenuItems = useMemo(() => {
        const menuItems = [
            ...convertToAntdMenuItems(footerMenuItems) ?? []
        ];

        menuItems?.push(
            {
                key: 'current-user',
                className: '__sidemenu-current-user',
                icon: <Avatar icon={<FAIcon prefix="fad" name="user" />} size={34} style={{ marginLeft: '-8px', backgroundColor: 'var(--primary-color)' }} />,
                label: !isSmartphone ? undefined :
                    <span className='__sidemenu-submenu __sidemenu-user'>
                        <div className='__sidemenu-submenu-content'>
                            <p className="text-disable-select __ellipsis-text" style={{ maxWidth: '110px' }}><span title={user?.username}>{user?.username}</span></p>
                        </div>
                        <LanguageSwitcher collapsed={false} />
                        <FAIcon style={{ cursor: 'pointer' }} prefix="fad" name="right-from-bracket" onClick={handleLogout} title={intl.formatMessage({ defaultMessage: 'Logout' })} />
                    </span>,
                onClick: () => undefined,
                children: !isSmartphone ? [
                    {
                        key: 'logout',
                        icon: <FAIcon prefix="fad" name="right-from-bracket" />,
                        label: <FormattedMessage defaultMessage={'Logout'} description={'Sidebar menu'} />,
                        onClick: handleLogout
                    },
                    {
                        key: 'languages',
                        className: '__sidemenu-languages',
                        icon: undefined,
                        label: <LanguageSwitcher collapsed />,
                        onClick: () => undefined
                    }

                ] : undefined
            }
        );

        return menuItems;
    }, [convertToAntdMenuItems, footerMenuItems, handleLogout, intl, isSmartphone, user?.username]);

    return (
        <div className='__sidemenu'>
            <div className='__sidemenu-logo-container'>
                <img src={LogoFull} className='__sidemenu-logo' alt="small Sunkhronos logo" />
            </div>
            <Divider className='__sidemenu-divider' />
            <div className='__sidemenu-main-menu'>
                <Menu
                    theme='light'
                    mode="inline"
                    selectedKeys={selectedKeys}
                    style={{ backgroundColor: 'transparent', overflowY: 'auto', marginBottom: '-12px' }}
                    items={antdMenuItems}
                    inlineCollapsed={!isSmartphone}
                    openKeys={openKeys}
                    onOpenChange={onOpenChange}
                />
            </div>
            <div>
                <Menu
                    theme='light'
                    mode="inline"
                    selectedKeys={selectedKeys}
                    style={{ backgroundColor: 'transparent' }}
                    items={antdFooterMenuItems}
                    inlineCollapsed={!isSmartphone}
                />
            </div>
        </div>
    );
};

export default SideMenu;